import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useSession } from "../../auth/AuthProvider";
import { createDistributor, updateDistributor } from "../../lib/operations";
import { useAdminData } from "./AdminData";

const EMPTY = {
  name: "",
  region: "",
  contactName: "",
  contactEmail: "",
  phone: "",
};

// Create (distributor = null) or edit a distributor.
export default function DistributorDialog({ open, distributor, onClose }) {
  const session = useSession();
  const navigate = useNavigate();
  const { distributors } = useAdminData();
  const [form, setForm] = useState(EMPTY);
  const [active, setActive] = useState(true);
  const [loginEmail, setLoginEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const editing = !!distributor;

  useEffect(() => {
    if (!open) return;
    setForm(
      editing
        ? Object.fromEntries(
            Object.keys(EMPTY).map((k) => [k, distributor[k] || ""]),
          )
        : EMPTY,
    );
    setActive(editing ? distributor.active !== false : true);
    setLoginEmail("");
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const regions = [
    ...new Set(distributors.map((d) => d.region).filter(Boolean)),
  ].sort();
  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await updateDistributor(distributor.id, { ...form, active });
        onClose();
      } else {
        const id = await createDistributor({
          fields: form,
          loginEmail: loginEmail.trim(),
          actor: session.email,
        });
        onClose();
        navigate(`/distributors/${id}`);
      }
    } catch (e) {
      console.error(e);
      setError(e.message || "Couldn't save the distributor.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <Box component="form" onSubmit={submit}>
        <DialogTitle>
          {editing ? "Edit distributor" : "Add distributor"}
        </DialogTitle>
        <DialogContent>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { sm: "1fr 1fr" },
              pt: 1,
            }}
          >
            <TextField
              label="Company name"
              value={form.name}
              onChange={set("name")}
              required
              autoFocus
            />
            <Autocomplete
              freeSolo
              options={regions}
              inputValue={form.region}
              onInputChange={(_, value) =>
                setForm((f) => ({ ...f, region: value }))
              }
              renderInput={(params) => (
                <TextField {...params} label="Region" required />
              )}
            />
            <TextField
              label="Contact person"
              value={form.contactName}
              onChange={set("contactName")}
            />
            <TextField
              label="Contact email"
              type="email"
              value={form.contactEmail}
              onChange={set("contactEmail")}
            />
            <TextField
              label="Phone"
              value={form.phone}
              onChange={set("phone")}
            />
          </Box>
          {!editing && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle2">
                Portal login (optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                The email address that will sign in to issue keys. You can add
                more people later.
              </Typography>
              <TextField
                fullWidth
                type="email"
                label="Login email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
              />
            </Box>
          )}
          {editing && (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={active}
                  onChange={(e) => setActive(e.target.checked)}
                />
              }
              label={
                active
                  ? "Active — can sign in and issue keys"
                  : "Paused — can't issue new keys"
              }
            />
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button color="inherit" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={busy || !form.name.trim() || !form.region.trim()}
          >
            {busy ? "Saving…" : editing ? "Save" : "Add distributor"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
